/**
 * SettingsPage — shared settings layout used by all tracker apps.
 * Provides a consistent container, title, and always renders PodSettings.
 * App-specific content is passed via `children` and rendered above PodSettings.
 *
 * Also exports CollapsibleSection for building expandable settings cards.
 */

import { useState } from 'react';
import PodSettings from './PodSettings.jsx';
import ProfileSettings from './ProfileSettings.jsx';

/**
 * Collapsible settings section — a card with a clickable header that
 * toggles the body open/closed with a smooth height animation.
 */
export function CollapsibleSection({ title, description, icon, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="bg-surface rounded-lg border border-border-subtle mb-4">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface-hover transition-colors min-h-[52px]"
            >
                {icon && <span className="text-text-tertiary flex-shrink-0">{icon}</span>}
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-sm font-semibold text-text-primary">{title}</h3>
                    {description && !open && (
                        <p className="font-body text-xs text-text-tertiary mt-0.5 truncate">{description}</p>
                    )}
                </div>
                <svg
                    className={`h-4 w-4 text-text-tertiary flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            <div
                className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
                <div className="overflow-hidden">
                    <div className="px-5 pb-5 pt-1">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function SettingsPage({ title = 'Settings', subtitle, children }) {
    return (
        <div className="bg-bg-primary min-h-screen py-8">
            <div className="mx-auto max-w-[600px] px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="font-display text-3xl font-bold tracking-tight text-text-primary">{title}</h1>
                    {subtitle && (
                        <p className="font-body mt-2 text-sm text-text-secondary">{subtitle}</p>
                    )}
                </div>

                {/* Profile */}
                <CollapsibleSection
                    title="Your Profile"
                    description="Update your display name and emoji avatar"
                    defaultOpen
                    icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>}
                >
                    <ProfileSettings />
                </CollapsibleSection>

                {/* Pod settings */}
                <CollapsibleSection
                    title="Your Pods"
                    description="Create or join a pod to share with friends"
                    defaultOpen
                    icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>}
                >
                    <PodSettings />
                </CollapsibleSection>

                {/* App-specific sections */}
                {children}
            </div>
        </div>
    );
}
